import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

interface IConfig {
  BASE_URL: string;
  PATHS: {
    [key: string]: string;
  };
}

interface IError {
  error: string;
}

class ApiManager {
  CONFIG: IConfig;
  signal?: AbortSignal;
  axiosInstance: AxiosInstance;

  constructor(
    CONFIG: IConfig,
    axiosInstance: AxiosInstance = axios,
    signal?: AbortSignal
  ) {
    this.CONFIG = CONFIG;
    this.signal = signal;
    this.axiosInstance = axiosInstance;
    return this;
  }

  #validateEntityName(entityName: string) {
    if (Object.hasOwnProperty.call(this.CONFIG.PATHS, entityName)) {
      return true;
    }
    return false;
  }

  #statusCodeHandler(error: AxiosError) {
    if (error.response?.status && error.response?.status) {
      const statusType = error.response.status.toString()[0];
      switch (statusType) {
        case '4': {
          return { error: 'Theres a client side error.' };
        }

        case '5': {
          return { error: 'Theres a server side error.' };
        }

        default: {
          return { error: 'Theres a unknown error.' };
        }
      }
    }
  }

  async #baseFetch<ResponseType>(
    entityName: string,
    asyncCallback: () => Promise<AxiosResponse<ResponseType>>
  ): Promise<AxiosResponse<ResponseType> | IError> {
    if (this.#validateEntityName(entityName)) {
      try {
        const response = await asyncCallback();

        return response;
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const validatedError = this.#statusCodeHandler(error);
          if (validatedError) {
            return validatedError;
          }
        }
        return { error: 'Unknown error' };
      }
    } else {
      return { error: 'Path not found' };
    }
  }

  async createEntity<DataType, ResponseType = DataType>(
    entityName: string,
    newEntity: DataType,
    headers?: AxiosRequestConfig<DataType>['headers'],
    parameters?: AxiosRequestConfig['params']
  ) {
    const response = await this.#baseFetch<ResponseType>(entityName, () =>
      this.axiosInstance({
        method: 'post',
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}`,
        data: newEntity,
        signal: this.signal,
        headers,
        params: parameters,
      })
    );

    return response;
  }

  async getEntity<ResponseType>(
    entityName: string,
    id: string | number,
    parameters?: AxiosRequestConfig['params']
  ) {
    const url = `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`;

    const response = await this.#baseFetch<ResponseType>(entityName, () =>
      this.axiosInstance({
        url,
        method: 'get',
        signal: this.signal,
        params: parameters,
      })
    );

    return response;
  }

  async getAllEntities<ResponseType>(
    entityName: string,
    parameters?: AxiosRequestConfig['params']
  ) {
    const url = `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}`;

    const response = await this.#baseFetch<ResponseType>(entityName, () =>
      this.axiosInstance({
        url,
        method: 'get',
        signal: this.signal,
        params: parameters,
      })
    );

    return response;
  }

  async replaceEntity<Type, DataType = Type>(
    entityName: string,
    id: string | number,
    newEntity: DataType,
    parameters?: AxiosRequestConfig['params']
  ) {
    const response = await this.#baseFetch<Type>(entityName, () =>
      this.axiosInstance({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'put',
        data: newEntity,
        signal: this.signal,
        params: parameters,
      })
    );

    return response;
  }

  async updateEntity<DataType, ResponseType = DataType>(
    entityName: string,
    id: string | number,
    updatedFields: DataType
  ) {
    const response = await this.#baseFetch<ResponseType>(entityName, () =>
      this.axiosInstance({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'patch',
        data: updatedFields,
        signal: this.signal,
      })
    );

    return response;
  }

  async deleteEntity<ResponseType>(
    entityName: string,
    id: string | number,
    parameters?: AxiosRequestConfig['params']
  ) {
    const response = await this.#baseFetch<ResponseType>(entityName, () =>
      this.axiosInstance({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'delete',
        signal: this.signal,
        params: parameters,
      })
    );

    return response;
  }
}

export default ApiManager;
